/**
 * @lancer-vtt/compendium — LCP ingestion and the queryable Compendium.
 *
 * Domain *types* and the in-memory index are importable by both backend and
 * frontend. The LCP-ZIP/IndexedDB loading code beside them is frontend-leaning;
 * keep type imports type-only on the backend.
 */

export * from "./schemas";
export * from "./pack";
export { Compendium, type OwnedItem } from "./compendium";
export {
  lancerDataAsRawPack,
  pilotLoadoutLimits,
  type PilotLoadoutLimits,
  basePilotStats,
  type BasePilotStats,
} from "./lancer-data";
export { longRimDataAsRawPack } from "./long-rim-data";
export {
  listManufacturers,
  listBackgrounds,
  listSkills,
  type Manufacturer,
  type Background,
  type Skill,
} from "./brand";
export {
  lcpZipToRawPack,
  v3ZipToRawPack,
  zipToRawPack,
  packLoadCommand,
  LcpArchiveError,
  type PackLoadCommand,
  type V3UnlinkedFeature,
} from "./lcp-zip";
export {
  InMemoryPackStore,
  type PackStore,
  type StoredPack,
} from "./pack-store";
export {
  MissingContentError,
  requireById,
} from "./missing-content";

import { Compendium } from "./compendium";
import { lancerDataAsRawPack } from "./lancer-data";
import { longRimDataAsRawPack } from "./long-rim-data";
import { zipToRawPack } from "./lcp-zip";
import { parsePack, type Pack } from "./pack";
import type { PackStore, StoredPack } from "./pack-store";

/**
 * Load a `.lcp` ZIP's bytes into a validated {@link Pack}: unzip → {@link RawPack}
 * (via {@link lcpZipToRawPack}) → validate+merge (via the existing {@link parsePack}).
 * The ZIP adapter only produces bytes→RawPack; all schema validation and the
 * later-pack-wins merge live in `parsePack`. Throws `LcpArchiveError` for a
 * structurally-broken archive, `PackValidationError` for schema failures.
 */
export function loadPackFromZip(bytes: Uint8Array): Pack {
  return parsePack(zipToRawPack(bytes));
}

/** Parse `@massif/lancer-data` (the core book) into a validated {@link Pack}. */
export function loadCorePack(): Pack {
  return parsePack(lancerDataAsRawPack());
}

/**
 * Parse `@massif/long-rim-data` (a free GPL-3.0 setting expansion: six mechs +
 * their gear and talents) into a validated {@link Pack}. Wired as a SECOND base
 * pack to exercise the multi-base-pack merge against real community content.
 */
export function loadLongRimPack(): Pack {
  return parsePack(longRimDataAsRawPack());
}

/**
 * The base packs bundled with the app, in merge order (later-pack-wins): the
 * core book first, then the Long Rim setting expansion. Proves the index merges
 * multiple base packs, not just core + a user-dropped `.lcp`.
 *
 * NOTE: neither base pack ships NPC content (`npc_*` counts stay 0). No free,
 * npm-installable Lancer NPC data exists — the official NPC `.lcp` is paywalled
 * with the Core Rulebook. The missing-content detector (`requireById`) is the
 * intended way to surface a referenced-but-unloaded NPC id.
 */
export function loadBasePacks(): Pack[] {
  return [loadCorePack(), loadLongRimPack()];
}

/**
 * Build a Compendium from the bundled base packs ({@link loadBasePacks}) plus any
 * additional already-validated packs, merged after them (later-pack-wins on id
 * collisions).
 */
export function loadCoreCompendium(extraPacks: Pack[] = []): Compendium {
  return new Compendium([...loadBasePacks(), ...extraPacks]);
}

/**
 * Build a {@link StoredPack} from raw `.lcp` bytes: validates the bytes through
 * the ZIP adapter + `parsePack` to derive the canonical pack id (so the store
 * key matches the Compendium provenance and a re-load overwrites in place).
 * Throws before persisting if the archive is broken or schema-invalid, so we
 * never store a pack that can't be re-parsed on reload.
 */
export function storedPackFromZip(bytes: Uint8Array, source?: string): StoredPack {
  const pack = parsePack(zipToRawPack(bytes));
  return { id: pack.id, source, bytes, storedAt: Date.now() };
}

/**
 * Rebuild a Compendium from the core book plus every pack persisted in `store`,
 * re-parsing each stored pack's bytes (the single validation path). Persisted
 * packs merge after core, in `store.getAll()` order (oldest-first), preserving
 * later-pack-wins. A stored pack whose bytes no longer parse is reported via
 * `onError` (so a bad pack can be surfaced/evicted) and skipped, rather than
 * failing the whole reload.
 */
export async function rebuildCompendiumFromStore(
  store: PackStore,
  onError?: (stored: StoredPack, err: unknown) => void,
): Promise<Compendium> {
  const stored = await store.getAll();
  const packs: Pack[] = [...loadBasePacks()];
  for (const s of stored) {
    try {
      packs.push(parsePack(zipToRawPack(s.bytes)));
    } catch (err) {
      if (onError) onError(s, err);
    }
  }
  return new Compendium(packs);
}
